/* eslint-disable @typescript-eslint/camelcase */
import React, { Component } from 'react'
import Question from './question'
// @ts-ignore
import ShareButtons from '@ta-interaktiv/react-share-buttons'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/table.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import s from './styles.module.scss'
import { SemanticUIColors } from './semanticUIColors'
import IntlMessageFormat from 'intl-messageformat'

declare global {
  interface Window {
    dataLayer: { event: string; event_action: string; event_label: string }[]
  }
}

/**
 * How the data is stored in
 * https://ta-user-feedback.firebaseio.com/v2/stories/.
 *
 * The property names are super short, so we need as little bandwidth as
 * possible.
 *
 * **Please note:** The previous version also saved the questions in the
 * database. This no longer happens. Votes are only tracked positionally:
 * the first number for the first question, the second number for the second
 * question, etc. **If you already have collected votes and then decide to
 * change the wording of the questions or their order, you will receive "wrong"
 * results.** You might want to delete the previously collected votes directly
 * in the Firebase Admin view and start over.
 *
 * @property p Number of participants
 * @property v Cast cumulative votes
 */
interface DatabaseFormat {
  p: number
  v: number[]
}

interface MessageList {
  [key: string]: string | string[] | MessageList
}

/**
 * Firebase Database Endpoint
 * @private
 * @constant
 * @type {string}
 */
const DB_ENDPOINT = 'https://ta-user-feedback.firebaseio.com/v2/stories'

/**
 * Translatable messages.
 */
const MESSAGES: { de: MessageList; fr: MessageList } = {
  de: {
    answerText:
      'Die meisten Leserinnen und Leser bewerten den Beitrag als sehr gut.' +
      ' Das freut uns.',
    questions: [
      'Wie lehrreich war der Beitrag?',
      'Wie unterhaltsam war er?',
      'Wie gefällt Ihnen die Gestaltung?'
    ],
    title: 'Wie war’s?',
    feedback: {
      thanks: 'Vielen Dank für Ihre Bewertung.',
      stats:
        'Bisher {respondentCount, plural, =0 {hat noch niemand} one' +
        ' {hat <strong>eine Person</strong>} other {haben <strong>#' +
        ' Personen</strong>}}' +
        ' abgestimmt.'
    },
    error: {
      noSave:
        'Vielen Dank für Ihre Bewertung! Leider gab es ein Problem' +
        ' mit der Kommunikation mit der Datenbank, und Ihre Bewertung konnte' +
        ' nicht gespeichert werden. Bitte versuchen Sie es' +
        ' später erneut.',
      noDownload:
        'Vielen Dank für Ihre Stimme. Leider konnten die' +
        ' Bewertungen der anderen Leserinnen und Leser nicht' +
        ' heruntergeladen werden. Wir können Ihnen deshalb im Moment' +
        ' die durchschnittliche Bewertung nicht anzeigen.'
    },
    submit: 'Bewerten'
  },
  fr: {
    answerText:
      'Une majorité de lectrices et lecteurs estime que ce contenu est très bon. Nous en sommes reconnaissants.',
    questions: [
      'Ce contenu était-il informatif?',
      'Etait-il divertissant?',
      'La mise en forme vous plaît-elle?'
    ],
    title: 'Comment votez-vous cette contribution?',
    feedback: {
      thanks: 'Merci pour votre évaluation.',
      stats:
        "{respondentCount, plural, =0 {Personne n'a encore} one {<strong>Une" +
        ' personne</strong> a} other {<strong># personnes</strong> ont}} noté ce' +
        ' contenu.'
    },
    error: {
      noSave:
        "Merci beaucoup pour votre évaluation! Malheureusement, la communication avec la base de données n'a pu être établie et vos notes n'ont pu être prises en compte. Nous vous prions de réessayer un peu plus tard.",
      noDownload:
        "Merci beaucoup pour votre évaluation. Malheureusement, les notes des autres utilisateurs n'ont pu être téléchargées. C'est la raison pour laquelle nous ne pouvons pas afficher de moyennes pour le moment."
    },
    submit: 'Confirmer'
  }
}

interface TextProps {
  /** The text people see when the overall
   * rating average is above the top 20%. */
  answerText?: string

  /**  A list of questions to ask the
   * readers. Defaults to 'Wie lehrreich war der Beitrag?', 'Wie unterhaltsam
   * war er?' and 'Wie gefällt Ihnen die Gestaltung?'. */
  questions?: string[]

  /** The main title. */
  title?: string
}

interface ComponentProps {
  /** The article ID related to this feedback. */
  articleId: string

  /** Additional class names to pass to the
   * component. */
  className?: string

  /** Whether the component is displayed on a dark background. */
  inverted?: boolean

  /** The locale in which to show the
   * component. Currently can be either French (`fr`) or German (`de`). */
  locale?: keyof typeof MESSAGES

  /** The amount of stars to use. */
  starAmount?: number

  /** The colors the stars should be shown in. */
  starColor?: SemanticUIColors
}

interface State {
  averageVotes?: number[]
  errorText?: string
  hasError: boolean
  numberOfParticipants?: number
  overallAverage?: number
  personalVotes: number[]
  readyToSubmit: boolean
  votingIsOver: boolean
}

export type Props = TextProps & ComponentProps

/**
 * Allows people to rate the standalone on various
 * questions.
 *
 * ```js
 * <UserFeedback
 *   articleId='20919286'
 *   starColor='yellow'
 *   title='Wie bewerten Sie diese Geschichte?'
 *   answerText='Die meisten Leserinnen und Leser bewerten den Artikel als sehr
 *               gut. Das freut uns.'
 *   questions={['Wie lehrreich war der Beitrag?', 'Wie unterhaltsam war er?',
 *               'Wie gefällt Ihnen die Gestaltung?']}
 * />
 * ```
 */
export class UserFeedback extends Component<Props, State> {
  // private static defaultProps = {
  //   className: '',
  //   inverted: false,
  //   locale: 'de',
  //   starAmount: 5,
  //   starColor: 'yellow'
  // }

  public constructor(props: Props) {
    super(props)
    let questions = props.questions
      ? props.questions
      : ((props.locale && MESSAGES[props.locale].questions) as string[])

    // Set up the state
    this.state = {
      // Fill the personalVotes with -1 matching the amount of questions
      personalVotes: questions.map(() => -1),
      readyToSubmit: false,
      hasError: false,

      // read the local storage item and convert it to a boolean
      votingIsOver: !!localStorage.getItem('vote-' + this.props.articleId)
    }
  }

  // region Handlers

  /**
   * Write the data after clicking submit button
   * @private
   */
  private submitHandler = () => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'click',
      event_label: 'region: userVoteButtonClicked/'
    })

    this.pushVotesToDB(this.state.personalVotes)
      .then(downloadedValues => {
        this.setVoteResults(downloadedValues)
        this.setState({
          votingIsOver: true
        })
      })
      .catch((error: Error) => {
        console.log(error.message, error)
      })
  }

  /** Set the state for all questions – with ratings from Question component
   * @private
   */
  private ratingHandler = (question: string, rating: number) => {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'click',
      event_label: 'region: userVote/' + rating
    })
    this.setState(state => {
      // Get the old value
      console.log(state)

      let questionRatings = state.personalVotes
      let questions = this.props.questions
        ? this.props.questions
        : ((this.props.locale &&
            MESSAGES[this.props.locale].questions) as string[])
      // ((this.props.locale && MESSAGES[this.props.locale].questions) as string[]) ||
      // We assume that the ratings are in the same order as the questions. So
      // we look up the index of the question and use that index to set the
      // selected rating
      questionRatings[questions.indexOf(question)] = rating

      return {
        personalVotes: questionRatings
      }
    }, this.checkIfSubmitReady)
  }

  // endregion

  /**
   * Increase a property in the database.
   * @private
   */
  private pushVotesToDB(ratings: number[]) {
    // Get original value
    return fetch(`${DB_ENDPOINT}/${this.props.articleId}.json`, {
      headers: { 'X-Firebase-ETag': 'true' }
    })
      .then(originalValueResponse =>
        originalValueResponse.json().then(previousValue => {
          // yes, this is very optimistic programming
          if (!originalValueResponse.ok) {
            throw Error(originalValueResponse.statusText)
          }

          // etag, used to check whether the values have changed between
          // getting the previous values and writing the new values
          const etag = originalValueResponse.headers.get('ETag')

          // No previous values? Apparently you're the first one to vote
          if (!previousValue) {
            return fetch(`${DB_ENDPOINT}/${this.props.articleId}.json`, {
              method: 'PUT',
              body: JSON.stringify({
                p: 1,
                v: ratings
              })
            }).then(writeResponse => {
              if (!writeResponse.ok) {
                throw Error(writeResponse.statusText)
              }

              localStorage.setItem(
                'vote-' + this.props.articleId,
                JSON.stringify(ratings)
              )
              return writeResponse.json()
            })
          }

          return this.writeToDatabase(ratings, previousValue, etag)
        })
      )
      .catch((error: Error) => {
        console.log(error.message, error)
        this.setState({
          hasError: true,
          errorText: 'error.noSave'
        })
        return { p: 1, v: ratings }
      })
  }

  /**
   * Write the increased value to the database – conditionally. This
   * function will call itself if the precondition isn't met and will just
   * keep on trying.
   * @private
   */
  private writeToDatabase(
    ratings: number[],
    previousValue: DatabaseFormat,
    eTag: string | null
  ): Promise<DatabaseFormat> {
    return fetch(`${DB_ENDPOINT}/${this.props.articleId}.json`, {
      headers: eTag ? { 'if-match': eTag } : undefined,
      method: 'PUT',
      body: JSON.stringify(
        this.updatePreviousValuesWithNewVotes(previousValue, ratings)
      )
    }).then(writeResponse =>
      writeResponse.json().then(json => {
        const responseStatus = writeResponse.status

        // Request has failed as data changed before, but luckily we get the new
        // data back right away
        if (responseStatus === 412) {
          const newValue = json
          const newEtag = writeResponse.headers.get('ETag')

          return this.writeToDatabase(ratings, newValue, newEtag)
        }

        if (responseStatus === 200) {
          // Save the vote status to local storage
          localStorage.setItem(
            'vote-' + this.props.articleId,
            JSON.stringify(ratings)
          )
          return json
        }
      })
    )
  }

  /**
   * Adds the votes to the cumulative votes of the previous voters.
   * @private
   */
  private updatePreviousValuesWithNewVotes(
    previousValue: DatabaseFormat,
    ratings: number[]
  ): DatabaseFormat {
    let cumulativeVotes = previousValue.v.map(
      (previousVote, index) => previousVote + ratings[index]
    )

    return {
      p: previousValue.p + 1,
      v: cumulativeVotes
    }
  }

  /**
   * Run a check to see whether the submit button should be enabled.
   * @private
   */
  private checkIfSubmitReady() {
    this.setState(state => {
      // If every value is above 0, we are ready to submit
      return { readyToSubmit: state.personalVotes.every(value => value > 0) }
    })
  }

  /**
   * Calculates the averages based on the cumulative votes and the number of
   * participants
   * @private
   */
  private setVoteResults(downloadedValues: DatabaseFormat) {
    // Calculate the averages
    let averages = downloadedValues.v.map(
      cumulativeVote => cumulativeVote / downloadedValues.p
    )

    let overallAverage =
      averages.reduce(
        (previousValue, currentValue) => previousValue + currentValue
      ) / averages.length

    this.setState({
      numberOfParticipants: downloadedValues.p,
      averageVotes: averages,
      overallAverage
    })
  }

  /**
   * Set up the capability we want to test for. This attaches an event
   * listener to the window (meaning: EVERYTHING!). Upon the first
   * interaction of that kind (like: a touch, or a mouse over, which, it
   * being the window, should happen pretty fast), it will call the appropriate
   * @param eventToTestFor
   * @param actionHandler
   */
  private setupTest(
    eventToTestFor: string,
    actionHandler: (isAble: boolean) => void
  ) {
    window.document.documentElement.addEventListener(
      eventToTestFor,
      function onFirstInteraction(event) {
        event.preventDefault()
        event.stopPropagation()

        // The require interaction has happened, call the handler function
        actionHandler(true)

        // Remove Event listener from window, it has done it's work
        window.document.documentElement.removeEventListener(
          eventToTestFor,
          onFirstInteraction,
          false
        )
      },
      false
    )
  }

  // Region React Lifecycle Methods

  public componentDidMount() {
    // Check if the user already voted or not. So he can't vote again.
    if (localStorage.getItem('vote-' + this.props.articleId)) {
      // Get data from the database
      return fetch(`${DB_ENDPOINT}/${this.props.articleId}.json`)
        .then(response =>
          response.json().then(values => {
            this.setVoteResults(values)
          })
        )
        .catch(error => {
          console.log(error)
          this.setState({
            hasError: true,
            errorText: 'error.noDownload'
          })

          this.setVoteResults({
            p: 1,
            v: JSON.parse(
              localStorage.getItem('vote-' + this.props.articleId) || '[]'
            )
          })
        })
    }
  }

  public render() {
    /**
     * Returns a translated string – or the prop when it's defined
     * @param key
     * @private
     */
    const t = (key: string): string | string[] => {
      return (
        this.props[key as keyof TextProps] ||
        key
          .split('.')
          .reduce<string | string[]>(
            (messageList, prop: string) =>
              messageList.hasOwnProperty(prop)
                ? messageList[prop as any]
                : 'TRANSLATED_STRING_NOT_FOUND: ' + key,
            MESSAGES[this.props.locale || 'de'] as any
          )
      )
    }

    const respondentMessage = new IntlMessageFormat(
      t('feedback.stats') as string,
      this.props.locale + '-CH'
    )
    const questions = t('questions') as string[]
    return (
      <div
        className={`user-feedback interactive ${s.userFeedback} ${
          this.props.className
        } ${this.props.inverted ? 'inverted' : ''}`}
      >
        {this.state.votingIsOver && !this.state.hasError && (
          <div className={`feedback-text ${s.feedbackText}`}>
            <span>{t('feedback.thanks')}</span>{' '}
            <span
              dangerouslySetInnerHTML={{
                __html: respondentMessage.format({
                  respondentCount: this.state.numberOfParticipants
                })
              }}
            />{' '}
            {this.state.overallAverage &&
              this.state.overallAverage >=
                (this.props.starAmount || 5) * 0.8 && (
                <span>{t('answerText')}</span>
              )}
          </div>
        )}

        {this.state.votingIsOver &&
          this.state.hasError &&
          this.state.errorText && (
            <div className={'feedback-text'}>{t(this.state.errorText)}</div>
          )}
        {!this.state.votingIsOver && (
          <div
            className={`ui small interactive ${
              this.props.inverted ? 'inverted' : ''
            } header`}
          >
            {t('title')}
          </div>
        )}
        <table
          id='vote-table'
          className={`ui interactive very basic ${
            this.props.inverted ? 'inverted' : ''
          } table`}
        >
          <tbody>
            {questions.map((question, i) => {
              let avg = {}
              if (this.state.averageVotes) {
                avg = {
                  averageRating: this.state.averageVotes[i]
                }
              }

              return (
                <Question
                  ratingHandler={this.ratingHandler}
                  starAmount={this.props.starAmount}
                  starColor={this.props.starColor}
                  question={question}
                  votingIsOver={this.state.votingIsOver}
                  {...avg}
                  inverted={this.props.inverted}
                  key={`question-${i}`}
                />
              )
            })}
          </tbody>
        </table>

        <div
          className={`ui vertical very fitted center aligned ${
            this.props.inverted ? 'inverted' : ''
          } basic segment`}
        >
          {this.state.votingIsOver && (
            <ShareButtons
              inverted={this.props.inverted}
              articleId={this.props.articleId}
              displayType='horizontal icons'
              itemClassName={this.props.starColor}
            />
          )}
          {!this.state.votingIsOver && (
            <button
              onClick={this.submitHandler}
              className={`ui button ${
                this.state.readyToSubmit === true ? '' : 'disabled'
              } ${this.props.inverted ? 'inverted' : ''} ${
                this.props.starColor
              }`}
            >
              {t('submit')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // endregion
}

export default UserFeedback
